import { useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  Label,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartFrame } from '../ChartFrame'
import { useChartSeries } from '../ChartSeriesContext'
import { useChartChrome } from '../cartesianChart'
import { formatChartValue } from '../chartFormatting'
import type { MetricUnit } from '../types'

import { buildRankedRows } from './buildRankedRows'
import { BarCategoryTick } from './BarCategoryTick'
import { BarDataTable } from './BarDataTable'
import { BarTooltipView } from './BarTooltipView'

// Layout — a fixed row height with a floor so a one- or two-row chart doesn't
// collapse. Bars run the full value width, so the right margin leaves room for
// the trailing value tick and the bar's rounded end cap.
const ROW_HEIGHT = 40
const MIN_HEIGHT = 160
const CHART_VERTICAL_PADDING = 24
const CATEGORY_AXIS_WIDTH = 116
const CHART_MARGIN = { top: 4, bottom: 4, left: 8, right: 72 }
const BAR_RADIUS = 2
const ROUNDED_RIGHT: [number, number, number, number] = [
  0,
  BAR_RADIUS,
  BAR_RADIUS,
  0,
]
// A labelled value axis reserves a taller band (tick row + caption below it), and
// the chart grows by the same allowance so the bars don't shrink to make room.
const VALUE_AXIS_HEIGHT = 48
const VALUE_AXIS_LABEL_ALLOWANCE = 28

const chartHeight = (rowCount: number): number =>
  Math.max(rowCount * ROW_HEIGHT + CHART_VERTICAL_PADDING, MIN_HEIGHT)

interface ValueAxisTickProps {
  /** Geometry + tick data injected by Recharts when used as the XAxis `tick`. */
  x?: number
  y?: number
  payload?: { value?: number }
  /** Formats the value with the series' unit (counts vs percentage). */
  unit?: MetricUnit
}

/**
 * Bottom value-axis tick. Subtle label with the fill set via CSS so it resolves /
 * re-themes in dark mode (the default Recharts tick uses a hardcoded colour).
 */
function ValueAxisTick({
  x = 0,
  y = 0,
  payload,
  unit,
}: ValueAxisTickProps): JSX.Element {
  return (
    <text
      x={x}
      y={y}
      dy="0.71em"
      textAnchor="middle"
      fontSize={12}
      style={{ fill: 'var(--color-text-subtle)' }}
    >
      {formatChartValue(payload?.value ?? 0, unit)}
    </text>
  )
}

export interface BarChartProps {
  /** Accessible name for the chart (e.g. "Followers by channel"). */
  ariaLabel: string
  /** Overlay shown when there's no data; the axes stay visible behind it. */
  emptyState?: ReactNode
  /** Disable enter animations *and* the category-tick hover tooltip — both are
   *  switched off together for the offscreen export snapshot (which renders with
   *  no `TooltipProvider`) and for stable tests. */
  disableAnimations?: boolean
  /** Optional caption centred under the value (X) axis, e.g. "Posts sent". Unlike
   *  the other charts' `yAxisLabel`, there's no default: a ranked chart's series
   *  labels are channel names, not the metric the axis measures. */
  xAxisLabel?: string
}

/**
 * The base ranked bar chart: one horizontal bar per series, drawn in the series'
 * own palette colour and sorted longest-first. Each series is reduced to a single
 * value per its declared `summaryMode` (latest reading for cumulative metrics like
 * Followers; range total for count metrics like Posts). Series — and their
 * colour/label/icon — come from {@link ChartSeriesContext}; colour is never a
 * prop. Values are text in the tooltip and the screen-reader data table (via
 * {@link ChartFrame}), and it's keyboard-navigable.
 */
export function BarChart({
  ariaLabel,
  emptyState,
  disableAnimations = false,
  xAxisLabel,
}: BarChartProps): JSX.Element {
  const series = useChartSeries()
  const { resolvedColors, gridColor, subtleTextColor } = useChartChrome(series)

  const rows = useMemo(() => buildRankedRows(series), [series])
  // Every series in one ranked chart shares a metric/unit (all followers, all
  // posts…), so the value axis + ticks format off the first series' unit.
  const unit = series[0]?.unit

  const height =
    chartHeight(rows.length) +
    (xAxisLabel != null ? VALUE_AXIS_LABEL_ALLOWANCE : 0)

  return (
    <ChartFrame
      ariaLabel={ariaLabel}
      emptyState={emptyState}
      height={height}
      dataTable={<BarDataTable caption={ariaLabel} />}
    >
      <RechartsBarChart
        data={rows}
        layout="vertical"
        margin={CHART_MARGIN}
        accessibilityLayer
        aria-label={ariaLabel}
      >
        <CartesianGrid horizontal={false} stroke={gridColor} />

        <XAxis
          type="number"
          tick={<ValueAxisTick unit={unit} />}
          axisLine={false}
          tickLine={false}
          height={xAxisLabel != null ? VALUE_AXIS_HEIGHT : undefined}
        >
          {xAxisLabel != null && (
            <Label
              value={xAxisLabel}
              position="insideBottom"
              offset={0}
              style={{
                fill: subtleTextColor,
                fontSize: 12,
                textAnchor: 'middle',
              }}
            />
          )}
        </XAxis>

        <YAxis
          type="category"
          dataKey="id"
          width={CATEGORY_AXIS_WIDTH}
          axisLine={false}
          tickLine={false}
          tick={<BarCategoryTick disableTooltip={disableAnimations} />}
        />

        <Tooltip
          cursor={{ fill: 'transparent' }}
          content={(props) => <BarTooltipView {...props} />}
        />

        <Bar
          dataKey="value"
          radius={ROUNDED_RIGHT}
          isAnimationActive={!disableAnimations}
        >
          {rows.map((row) => (
            <Cell key={row.id} fill={resolvedColors[row.id]} />
          ))}
        </Bar>
      </RechartsBarChart>
    </ChartFrame>
  )
}
